/** Sticky action bar for edit mode: dirty state + Save draft + Exit.
 * Task 15 adds debounced Validate findings + Publish gating to this bar. */
import { useState } from 'react'
import { useConfigureSession } from '@/hooks/useConfigureSession'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { Button, Switch } from '@/components/ui'
import { describeOverlayPath } from '@/utils/workflowConfigView'
import { ConfigConflictBanner } from './ConfigConflictBanner'
import { PublishDialog } from './PublishDialog'
import { RebaseConflictsLine } from './RebaseConflictsLine'

export function ConfigureBar({ workflowId, onReviewPath }: {
  workflowId: string
  /** SA-1410: opens the setting a keep-edits Reload let the published value win. */
  onReviewPath: (path: string) => void
}) {
  const store = useWorkflowConfigStore()
  const session = useConfigureSession(workflowId, store)
  const { validation, advanced, setAdvanced, base } = store
  const [showPublish, setShowPublish] = useState(false)
  const { dirty, overlayEmpty, canPublish, validateFailed, conflict } = session

  // opaque base + primary tint overlay — sticky bar must not let content bleed through
  return (
    <div className="sticky bottom-0 z-10 space-y-2 rounded-xl border bg-card bg-linear-to-t from-primary/10 to-primary/10 border-primary/20 px-4 py-2">
      {validateFailed && (
        <p className="text-xs text-warning-strong">
          Couldn't check this configuration with the server — it will retry on your next change.
        </p>
      )}
      {validation && !validation.valid && (
        <ul className="text-xs text-destructive space-y-0.5">
          {validation.findings.map((f, i) => {
            const d = describeOverlayPath(f.path, base, store.overlay)
            return (
              <li key={i}>
                <span title={f.path} className={d.resolved ? undefined : 'font-mono'}>
                  {d.label}
                </span>
                : {f.message}
              </li>
            )
          })}
        </ul>
      )}
      {conflict && (
        <ConfigConflictBanner conflict={conflict} reloading={session.reloading} onReload={session.handleReload} />
      )}
      <RebaseConflictsLine
        paths={session.rebaseConflicts}
        describe={(path) => describeOverlayPath(path, base, store.overlay)}
        onReview={onReviewPath}
      />
      {session.saveError && <p className="text-xs text-destructive">{session.saveError}</p>}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="text-xs text-foreground">
            Customizing this workflow.{' '}
            {dirty ? <span className="font-medium">Unsaved changes.</span> : <span>Draft saved.</span>}
          </p>
          <label className="flex items-center gap-1.5 text-xs text-foreground">
            <Switch
              size="sm"
              aria-label="advanced options"
              checked={advanced}
              onCheckedChange={setAdvanced}
            />
            Advanced
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => store.stopEditing()}>
            Exit
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!dirty || session.saving || conflict !== null}
            isLoading={session.saving}
            onClick={session.handleSave}
          >
            {session.saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            size="sm"
            disabled={!canPublish}
            title={conflict ? 'Reload first' : dirty ? 'Save the draft first' : overlayEmpty ? 'Nothing customized yet' : undefined}
            onClick={() => setShowPublish(true)}
          >
            Publish
          </Button>
        </div>
      </div>
      {showPublish && validation && (
        <PublishDialog
          workflowId={workflowId}
          diff={validation.diff}
          describe={(path) => describeOverlayPath(path, base, store.overlay)}
          basis={session.basis}
          onConflict={session.raiseConflict}
          onClose={() => setShowPublish(false)}
        />
      )}
    </div>
  )
}
